// Application-level configuration context.
//
// Owns the small slice of *server-side* configuration the UI needs at boot — currently
// just the display timezone — so every component that renders a timestamp can read the
// same IANA zone id the backend uses for serialization.
//
// Architecture:
//   backend application.yml `app.display-timezone`
//     -> Java DateTimeService (validation + safe fallback live SOLELY here)
//       -> GET /api/ticket/no-auth/config/display-timezone
//         -> AppConfigProvider (this file, fetch ONCE on mount)
//           -> useDisplayTimezone() consumed by TicketDetail.js / MyTickets.js / ...
//             -> Intl.DateTimeFormat({ timeZone: <that value> })
//
// Ownership rule (single source of truth):
//   The backend is the *only* place that knows what the business display timezone is —
//   including the safe fallback when the configured value is missing or invalid. The
//   frontend deliberately ships with NO business timezone literal: no default, no fallback,
//   no inferred zone. If the backend cannot be reached, `displayTimezone` stays `null` and
//   consumers must show a loading placeholder rather than invent a wall-clock value.
//
// Resilience (intentional):
//   * `displayTimezone` is `null` until the backend responds.
//   * If the backend is unreachable, returns a malformed payload, or returns a non-string,
//     the value stays `null` and a warning is logged. The UI degrades visibly (renders a
//     placeholder), it does not silently render the wrong wall-clock.
//   * Single fetch on mount; no retry storm.
//
// Why this matters:
//   With a frontend-side fallback, a backend deploy that changes `app.display-timezone`
//   would still work for users whose config fetch succeeded but silently render the OLD
//   zone for any user whose fetch failed. Removing the frontend fallback eliminates that
//   class of bug — the only zone the UI can ever display is the one the backend is
//   actively serving.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiGet } from '../services/apiClient';

// Default context value carries `null` so consumers used outside the provider get the
// same "not yet available" semantics as during initial load. There is intentionally no
// business timezone literal anywhere in this file.
//
// `featureFlags` is an additive, fail-closed map: missing keys / failed fetches resolve
// to `false`, so any UI element gated by a flag stays hidden if the backend cannot be
// reached. New flags can be added on the backend without coordinated frontend deploys.
const AppConfigContext = createContext({
  displayTimezone: null,
  featureFlags: {},
  isLoading: true,
});

export const AppConfigProvider = ({ children }) => {
  // `null` means "backend value not yet available" (loading, failed, or malformed).
  // Consumers MUST handle this — see useDisplayTimezone() JSDoc below.
  const [displayTimezone, setDisplayTimezone] = useState(null);
  // Empty map until the /features fetch settles. See `useFeatureFlag` for the
  // fail-closed read semantics we rely on UI side.
  const [featureFlags, setFeatureFlags] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // Both bootstrap calls are independent — fetch them in parallel so an outage in
    // one (e.g. /features added on a hotfix branch that didn't ship) doesn't block the
    // other. Each is wrapped in its own try/catch and degrades to its safe default
    // (null timezone, empty flag map).
    (async () => {
      const tzPromise = (async () => {
        try {
          // ResponseBean shape: { status, message, data: { displayTimezone }, success }
          const response = await apiGet('/gateway/api/ticket/no-auth/config/display-timezone');
          const tz = response?.data?.displayTimezone;
          if (cancelled) return;
          if (typeof tz === 'string' && tz.trim().length > 0) {
            setDisplayTimezone(tz.trim());
          } else {
            // Backend reachable but payload not usable. Keep `null` so the UI shows a
            // placeholder instead of inventing a zone.
            console.warn(
              '[AppConfig] /config/display-timezone returned no usable displayTimezone; ' +
                'leaving displayTimezone=null. Timestamp UI will show a loading placeholder ' +
                'until the backend serves a valid value.'
            );
          }
        } catch (err) {
          if (cancelled) return;
          // Network error / 5xx / 404. Keep `null`. We deliberately do NOT fall back to a
          // hardcoded zone — that would risk silently rendering the wrong business
          // wall-clock when the backend is being deploy-rotated to a different zone.
          console.warn(
            '[AppConfig] failed to load display timezone from backend; ' +
              'leaving displayTimezone=null. Timestamp UI will show a loading placeholder ' +
              'until the backend is reachable again. Cause:',
            err?.message || err
          );
        }
      })();

      const flagsPromise = (async () => {
        try {
          // ResponseBean shape: { status, message, data: { ticketRelations: bool, ... }, success }
          const response = await apiGet('/gateway/api/ticket/no-auth/config/features');
          const flags = response?.data;
          if (cancelled) return;
          if (flags && typeof flags === 'object') {
            // Coerce every value to a boolean. Anything non-boolean (string, null, missing)
            // collapses to `false` so feature-gated UI is fail-closed.
            const normalized = {};
            Object.keys(flags).forEach((k) => {
              normalized[k] = flags[k] === true;
            });
            setFeatureFlags(normalized);
          } else {
            console.warn(
              '[AppConfig] /config/features returned a non-object payload; ' +
                'all feature flags will resolve to false until the backend serves a valid value.'
            );
          }
        } catch (err) {
          if (cancelled) return;
          // 404 is the expected response on backends predating the /features endpoint;
          // log at debug verbosity so we don't spam consoles during a rolling deploy.
          if (err?.response?.status === 404 || err?.status === 404) {
            console.debug(
              '[AppConfig] /config/features not available (backend predates feature flags). ' +
                'All flags will resolve to false.'
            );
          } else {
            console.warn(
              '[AppConfig] failed to load feature flags from backend; ' +
                'all flags will resolve to false. Cause:',
              err?.message || err
            );
          }
        }
      })();

      await Promise.all([tzPromise, flagsPromise]);
      if (!cancelled) setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppConfigContext.Provider value={{ displayTimezone, featureFlags, isLoading }}>
      {children}
    </AppConfigContext.Provider>
  );
};

/**
 * Returns the IANA zone id the backend is configured to display timestamps in.
 *
 * IMPORTANT: this hook can return `null` — both during initial load and when the backend
 * config fetch has failed. Callers MUST handle the null case before passing the value to
 * `Intl.DateTimeFormat` / `toLocaleString({ timeZone })`, because passing `null` as the
 * `timeZone` option throws `RangeError: Invalid time zone specified: null`.
 *
 * Recommended pattern:
 *   const displayTimezone = useDisplayTimezone();
 *   ...
 *   {displayTimezone
 *     ? new Date(iso).toLocaleString('en-GB', { timeZone: displayTimezone, hour12: false })
 *     : '…'}
 *
 * Why null and not a fallback string: the backend is the single source of truth for the
 * business timezone, including its fallback. A frontend-side fallback could mask a real
 * outage and silently render the wrong wall-clock.
 */
export const useDisplayTimezone = () => {
  return useContext(AppConfigContext).displayTimezone;
};

/**
 * Full context accessor — returns `{ displayTimezone, featureFlags, isLoading }`. Use
 * this when you need to distinguish "still loading" from "loaded but failed" (both
 * surface as `displayTimezone === null` and `featureFlags === {}`, but `isLoading` is
 * `false` once both bootstrap fetches have settled).
 */
export const useAppConfig = () => useContext(AppConfigContext);

/**
 * Generic feature-flag hook with fail-closed semantics. Returns `true` ONLY when the
 * backend reported the named flag as exactly `true`; everything else (loading, fetch
 * failed, flag absent, non-boolean value) returns `false`.
 *
 * Usage:
 *   const ticketRelationsEnabled = useFeatureFlag('ticketRelations');
 *   if (!ticketRelationsEnabled) return null; // hide the whole UI surface
 *
 * Why fail-closed: a flag that defaults to `true` on fetch failure could surface a
 * partially-deployed feature in production for users whose first config fetch missed.
 */
export const useFeatureFlag = (name) => {
  const { featureFlags } = useContext(AppConfigContext);
  return featureFlags?.[name] === true;
};

/**
 * Convenience wrapper for the V1 parent-child ticket relationships flag.
 * Equivalent to `useFeatureFlag('ticketRelations')`; named so call sites at the panel /
 * modal / queue-badge boundaries read intent-first instead of string-first.
 */
export const useTicketRelationsEnabled = () => useFeatureFlag('ticketRelations');

export default AppConfigContext;
